import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

export default function Footer({ city }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const cityName = city?.name || "Philadelphia";
  const coords = city?.coords || "39.9526°N 75.1652°W";

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. First dispatch lands Friday.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#0E1117]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid gap-12 lg:grid-cols-2"
        >
          <div>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-neon-lime" fill="currentColor" />
              <span className="font-display text-sm font-bold uppercase tracking-widest">
                {cityName} <span className="text-neon-lime">//</span> After Dark
              </span>
            </div>
            <h2 className="mt-6 font-editorial text-3xl uppercase leading-tight tracking-wide sm:text-4xl">
              Never miss a<br /><span className="text-neon-lime text-glow-lime">Friday night</span> again.
            </h2>
            <p className="mt-4 max-w-md text-sm text-zinc-400">
              One email a week. The five best things happening in {cityName} after dark — no fluff, no tourist traps.
            </p>
          </div>
          <div className="flex flex-col justify-center">
            <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@visiting-from.com"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full border border-white/10 bg-white/5 px-6 py-4 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-lime/60 focus:ring-1 focus:ring-neon-lime/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-neon-lime px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(34,197,94,0.45)] disabled:opacity-60"
              >
                <Send className="h-4 w-4" /> Join
              </button>
            </form>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
              {coords} — EST. FOR THE OUT-OF-TOWNERS
            </p>
          </div>
        </motion.div>
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-8 sm:flex-row">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
            © 2026 {cityName} After Dark — Go Birds.
          </span>
          <a href="/admin" data-testid="footer-admin-link" className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600 transition-colors hover:text-neon-lime">
            Admin Access
          </a>
        </div>
      </div>
    </footer>
  );
}
